import ButtonGroup from '@components/forms/ButtonGroup'
import ImageWithFallback from '@components/image/ImageWithFallback'
import SearchTable from '@components/search/SearchTable'
import ConnectEmptyState from '@components/shared/ConnectEmptyState'
import InlineNotification from '@components/shared/InlineNotification'
import Modal from '@components/shared/Modal'
import SheenLoader from '@components/loading/SheenLoader'
import store from 'stores/store'
import WalletBalances from '@components/wallet/WalletBalances'
import {
  MagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid'
import { useWallet } from '@solana/wallet-adapter-react'
import { useQuery } from '@tanstack/react-query'
import { localStorageKey, useLocalStorage } from 'hooks/useLocalStorageState'
import { SearchResult, useSearchResults } from 'hooks/useSearchResults'
import Link from 'next/link'
import { useCallback, useState, ChangeEvent, useEffect, useRef } from 'react'
import { ModalProps } from 'types/modal'
import { POPULAR_SWAP_TOKENS } from 'utils/constants'
import { IconButton, LinkButton } from '@components/shared/Button'
import { TokenMetadataItem, TokenMinimalData } from 'types/token'
import { useTranslation } from 'next-i18next'
import { milliseconds } from 'date-fns'
import usePrevious from 'hooks/usePrevious'
import { fetchBirdeyeTrending } from 'apis/birdeye/tokens'

export type SearchModalType =
  | 'search'
  | 'inputToken'
  | 'outputToken'
  | 'quoteToken'

interface SearchModalProps {
  onResultClick: (mint: string, token?: TokenMetadataItem) => void
  type: SearchModalType
  focusSearchOnShow?: boolean
}

type ModalCombinedProps = SearchModalProps & ModalProps

const fetchPopularTokens = async (): Promise<SearchResult[]> => {
  try {
    return await fetchBirdeyeTrending(20)
  } catch (e) {
    console.error('failed to fetch popular tokens', e)
    return []
  }
}

export const RECENTLY_SEARCHED_KEY = localStorageKey(
  'recentlySearched-0.3',
  [] as TokenMinimalData[],
)

const SearchModal = ({
  isOpen,
  onClose,
  onResultClick,
  type,
  focusSearchOnShow = false,
}: ModalCombinedProps) => {
  const [searchString, setSearchString] = useState('')
  const isSearch = type === 'search'
  const TAB_VALUES = ['popular', 'balances'] as const
  const [activeTab, setActiveTab] = useState(
    isSearch ? TAB_VALUES[0] : TAB_VALUES[1],
  )
  const { connected } = useWallet()
  const { results, loading: loadingResults } = useSearchResults(searchString)
  const [recentlySearched, setRecentlySearched] = useLocalStorage(
    RECENTLY_SEARCHED_KEY,
  )
  const setMarketAndSwapToken = store((s) => s.actions.setMarketAndSwapToken)
  const inputTokenMint = store((s) => s.swap.inputToken.mint)
  const outputTokenMint = store((s) => s.swap.outputToken.mint)
  const baseTokenMint = store((s) => s.market.baseToken.mint)
  const { t } = useTranslation('search')
  const searchInputRef = useRef<HTMLInputElement>(null)

  const { data: popularTokens, isInitialLoading: loadingPopularTokens } =
    useQuery({
      queryKey: ['token-search-results'],
      queryFn: fetchPopularTokens,
      cacheTime: milliseconds({ minutes: 30 }),
      staleTime: milliseconds({ minutes: 30 }),
      retry: 3,
      refetchOnWindowFocus: false,
    })

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value)
  }, [])

  const handleRecentSearchClick = useCallback(
    async (tokenReference: TokenMinimalData) => {
      setMarketAndSwapToken(tokenReference.mint)
      onClose()
    },
    [onClose, setMarketAndSwapToken],
  )

  const isTokenDisabled = useCallback(
    (token: SearchResult) => {
      const tokenMint = token.mint
      switch (type) {
        case 'inputToken':
          return tokenMint === outputTokenMint
        case 'outputToken':
          return tokenMint === inputTokenMint
        case 'quoteToken':
          return tokenMint === baseTokenMint
        default:
          return false
      }
    },
    [baseTokenMint, inputTokenMint, outputTokenMint, type],
  )

  const prevIsOpen = usePrevious(isOpen)
  useEffect(() => {
    if (focusSearchOnShow && searchInputRef.current && !prevIsOpen && isOpen) {
      searchInputRef.current.focus()
    }
  }, [focusSearchOnShow, isOpen, prevIsOpen])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      panelClassNames="max-h-screen !p-0 !max-w-2xl"
    >
      <div className="h-screen sm:h-[590px]">
        <div className="relative h-14 w-full">
          <input
            ref={searchInputRef}
            className="h-14 w-full bg-th-input-bkg pl-12 pr-10 text-left text-base text-th-fgd-1 focus:outline-none focus-visible:border-th-fgd-4 sm:rounded-t-md md:hover:border-th-input-border-hover md:hover:focus-visible:border-th-fgd-4"
            placeholder={t('search-tokens')}
            value={searchString}
            onChange={handleInputChange}
          />
          <MagnifyingGlassIcon className="absolute left-4 top-[calc(50%-10px)] size-5 text-th-fgd-3" />
          {searchString.length ? (
            <LinkButton
              className="absolute right-14 top-1/2 h-14 -translate-y-1/2 bg-th-input-bkg pl-2 text-xs text-th-fgd-3 sm:right-10"
              onClick={() => setSearchString('')}
            >
              {t('clear')}
            </LinkButton>
          ) : null}
        </div>
        <div className="md:px-6">
          {isSearch && recentlySearched.length ? (
            <div className="mt-6 px-4 md:px-0">
              <p className="mb-2">{t('recent')}</p>
              <div className="grid grid-cols-3 gap-2">
                {recentlySearched.map((token) => {
                  const { mint, symbol, image_url } = token
                  return (
                    <Link
                      className="flex h-10 items-center rounded-full bg-th-bkg-3 px-2.5 md:hover:bg-th-bkg-4"
                      href={`/token/${mint}`}
                      key={mint}
                      onClick={() => handleRecentSearchClick(token)}
                    >
                      {image_url ? (
                        <ImageWithFallback
                          className="mr-2"
                          height={24}
                          width={24}
                          src={image_url}
                          alt={`${symbol ?? ''} token logo`}
                        />
                      ) : (
                        <QuestionMarkCircleIcon className="mr-2 size-6 text-th-fgd-4" />
                      )}
                      <p className="truncate text-sm font-bold leading-none text-th-fgd-1">
                        {symbol}
                      </p>
                      <div className="ml-auto flex">
                        <XMarkIcon
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setRecentlySearched(
                              recentlySearched.filter((x) => x.mint !== mint),
                            )
                          }}
                          className="size-5"
                        />
                      </div>
                    </Link>
                  )
                })}
              </div>
            </div>
          ) : null}
          <div className="mt-6">
            <div className="mb-2 flex items-center justify-between px-4 sm:mb-3 md:px-0">
              {searchString.length >= 2 ? (
                <p>
                  {results?.length
                    ? t('results-count', { count: results.length })
                    : ''}
                </p>
              ) : (
                <div className="w-48">
                  <ButtonGroup
                    activeValue={activeTab}
                    values={TAB_VALUES}
                    names={[t('popular'), t('balances')]}
                    onChange={(v) => setActiveTab(v)}
                  />
                </div>
              )}
              {!isSearch ? (
                <div className="flex items-center gap-3">
                  {POPULAR_SWAP_TOKENS.map((token) => (
                    <IconButton
                      key={token.mint}
                      onClick={() => onResultClick(token.mint, token)}
                      size="small"
                    >
                      <ImageWithFallback
                        src={token.image_url}
                        alt={token.symbol || ''}
                        height={20}
                        width={20}
                      />
                    </IconButton>
                  ))}
                </div>
              ) : null}
            </div>
            <div
              className={`thin-scroll ${
                !isSearch
                  ? 'h-[calc(100vh-122px)] sm:h-[466px]'
                  : recentlySearched?.length
                    ? 'h-[calc(100vh-212px)] sm:h-[374px]'
                    : 'h-[calc(100vh-116px)] sm:h-[470px]'
              } overflow-y-auto overflow-x-hidden`}
            >
              {Array.isArray(results) && results?.length ? (
                <SearchTable
                  tokens={results}
                  onClick={onResultClick}
                  isDisabled={isTokenDisabled}
                />
              ) : searchString.length < 2 ? (
                <>
                  {activeTab === 'balances' ? (
                    connected ? (
                      <div className="px-3">
                        <WalletBalances
                          onClick={onResultClick}
                          isDisabled={(token) =>
                            isTokenDisabled({
                              ...token,
                              volume_24h_usd: 0,
                              latest_price_usd: 0,
                            })
                          }
                        />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center rounded-xl border border-th-bkg-4 p-6">
                        <ConnectEmptyState text={t('connect-to-see')} />
                      </div>
                    )
                  ) : popularTokens && popularTokens?.length ? (
                    <SearchTable
                      tokens={popularTokens}
                      onClick={onResultClick}
                      isDisabled={isTokenDisabled}
                    />
                  ) : loadingPopularTokens ? (
                    <Loading />
                  ) : (
                    <InlineNotification
                      type="error"
                      desc={t('failed-to-load')}
                    />
                  )}
                </>
              ) : loadingResults ? (
                <Loading />
              ) : typeof results === 'string' ? (
                <InlineNotification type="error" desc={results} />
              ) : (
                <div className="flex items-center justify-center rounded-xl border border-th-bkg-4 p-6">
                  <p>
                    {t('no-tokens-found.message', {
                      forSearchString: searchString.length
                        ? t('no-tokens-found.for-search-string', {
                            searchString,
                          })
                        : '',
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default SearchModal

const Loading = () => {
  return (
    <div className="mt-6 space-y-0.5">
      {Array.from({ length: 4 }, (_, i) => (
        <SheenLoader className="flex flex-1" key={i}>
          <div className="h-14 w-full bg-th-bkg-2" />
        </SheenLoader>
      ))}
    </div>
  )
}
